import { useEffect, useState } from "react";
import type { AgentDTO, AgentScopeDTO, PromptPreviewDTO } from "../../shared/types";
import { get } from "../lib/api";
import { Dialog } from "./Dialog";
import { Button, ErrorText, Field, inputCls, SectionHead } from "./ui";

/** The text as the agent reads it: one box under its heading, never a box inside a box. */
const textCls = "scroll-thin max-h-96 overflow-auto rounded-xl bg-fill-subtle p-3 font-mono text-xs break-words whitespace-pre-wrap";

/**
 * "Preview prompt" on an agent's settings (AIT-27): its system prompt exactly as its next run gets it, as saved, and the
 * run block of sample runs built by the same code from real records — the admin looking, in the web chat, and for an
 * agent with a scope type one run of a scope it works on (chosen here) beside its agent level.
 */
export function PromptPreview({ agent }: { agent: Pick<AgentDTO, "id" | "name" | "scopeTypes"> }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("");
  const [scopes, setScopes] = useState<AgentScopeDTO[]>([]);
  const [data, setData] = useState<PromptPreviewDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const typed = agent.scopeTypes.length > 0;

  useEffect(() => {
    if (!open || !typed) return;
    get<AgentScopeDTO[]>(`/api/agents/${agent.id}/scopes?detail=1`).then(setScopes, () => setScopes([]));
  }, [open, typed, agent.id]);
  useEffect(() => {
    if (!open) return;
    setError(null);
    get<PromptPreviewDTO>(`/api/agents/${agent.id}/prompt-preview${scope ? `?scope=${encodeURIComponent(scope)}` : ""}`).then(setData, (err) => setError((err as Error).message));
  }, [open, scope, agent.id]);

  return (
    <>
      <Button onClick={() => setOpen(true)}>Preview prompt</Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        size="xl"
        title={`${agent.name}'s prompt`}
        description="Exactly as its next run gets it, as saved: save your changes first to see them here."
        primary={<Button variant="primary" onClick={() => setOpen(false)}>Done</Button>}
        cancel={false}
      >
        <div className="space-y-5">
          <ErrorText error={error} />
          {!data && !error && <p className="text-sm text-muted">Building the prompt…</p>}
          {data && (
            <>
              <section>
                <SectionHead
                  title="System prompt"
                  hint={`The same for every run of this version: ${data.chars.toLocaleString("en-US")} characters, about ${data.tokens.toLocaleString("en-US")} tokens.`}
                />
                <pre className={textCls}>{data.system}</pre>
              </section>
              {typed && scopes.length > 0 && (
                <Field label="Scope run on" hint="Which of its scopes the sample scope run works on.">
                  <select className={inputCls} value={scope} onChange={(e) => setScope(e.target.value)}>
                    <option value="">The one it worked on last</option>
                    {scopes.map((s) => (
                      <option key={s.id} value={s.key}>
                        {s.displayName}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {data.samples.map((s) => (
                <section key={s.label}>
                  <SectionHead title={s.label} hint="The run block at the top of the first message of such a run." />
                  <pre className={textCls}>{s.run}</pre>
                </section>
              ))}
            </>
          )}
        </div>
      </Dialog>
    </>
  );
}
